import { useCallback, useEffect, useMemo, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { LoginForm } from './components/LoginForm'
import { AccountPanel } from './components/AccountPanel'
import { ConfigurationPanel } from './components/ConfigurationPanel'
import { useAccess } from './hooks/useAccess'
import { useAccounts } from './hooks/useAccounts'
import { useConfiguration } from './hooks/useConfiguration'
import { StartupScreen } from '@/components/startup-screen'
import { Alert, Avatar, Button, Popover, Space, Spin, Tabs, Tag, Tooltip, Typography } from 'antd'
import { FileTextOutlined, LogoutOutlined, ReloadOutlined, SettingOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons'
import './styles.css'

const ACCOUNT_ICON = <UserOutlined aria-hidden />
const LOGOUT_ICON = <LogoutOutlined aria-hidden />
const ACCOUNTS_ICON = <TeamOutlined aria-hidden />
const CONFIGURATION_ICON = <FileTextOutlined aria-hidden />

const Accounts = ({ userId, revalidate }: { userId: string; revalidate: () => void }) => {
  const model = useAccounts(userId, revalidate)
  return <AccountPanel model={model} />
}
const Configuration = ({ userId, revalidate }: { userId: string; revalidate: () => void }) => {
  const model = useConfiguration(userId, revalidate)
  return <ConfigurationPanel model={model} />
}
const Controls = ({ userId, revalidate }: { userId: string; revalidate: () => void }) => {
  // Both panels share the existing query owner; changing tabs must not discard edit drafts.
  const items = useMemo(() => [
    { key: 'accounts', label: '账号分配', icon: ACCOUNTS_ICON, children: <Accounts userId={userId} revalidate={revalidate} /> },
    { key: 'configuration', label: '配置文件', icon: CONFIGURATION_ICON, children: <Configuration userId={userId} revalidate={revalidate} /> },
  ], [revalidate, userId])
  return <Tabs items={items} className="instance-administration__navigation" />
}
const Administration = () => {
  const access = useAccess()
  const { logout: performLogout } = access
  const logout = useCallback(() => { void performLogout() }, [performLogout])
  const identity = useMemo(() => <div className="instance-administration__account-details">
    <Typography.Text type="secondary">管理员</Typography.Text>
    <Typography.Text strong>{access.userId}</Typography.Text>
  </div>, [access.userId])
  // This entry owns first paint without starting the procurement authentication/Host chain.
  const startupScreen = <StartupScreen phase={access.phase === 'checking' ? 'authenticating' : 'ready'} onRetry={access.revalidate} />
  if (access.phase === 'checking') return <>{startupScreen}<div className="instance-administration__checking" role="status"><Spin />正在验证身份</div></>
  if (access.phase !== 'ready') return <>
    {startupScreen}
    <div className="instance-administration__login">
      {access.error ? <Alert type="error" showIcon title={access.error} role="alert" /> : null}
      <LoginForm pending={access.pending} onLogin={access.login} />
      <Button type="text" icon={<ReloadOutlined />} onClick={access.revalidate}>重新验证</Button>
    </div>
  </>
  return <>{startupScreen}<header className="instance-administration__header">
    <div className="instance-administration__title"><span className="instance-administration__mark"><SettingOutlined /></span><h1>设置</h1></div>
    <div className="instance-administration__header-actions">
      <Space wrap className="instance-administration__identity"><Tag>管理员</Tag><Typography.Text>{access.userId}</Typography.Text></Space>
      <Popover trigger="click" content={identity} placement="bottomRight">
        <Button type="text" aria-label="查看当前账号" className="instance-administration__account-toggle">
          <Avatar size={28} icon={ACCOUNT_ICON} />
        </Button>
      </Popover>
      <Tooltip title="退出">
        <Button aria-label="退出" icon={LOGOUT_ICON} onClick={logout} className="instance-administration__logout">
          <span className="instance-administration__logout-label">退出</span>
        </Button>
      </Tooltip>
    </div>
  </header>
    <Controls userId={access.userId} revalidate={access.revalidate} /></>
}
export default function InstanceAdministrationPage() {
  const [client] = useState(() => new QueryClient())
  useEffect(() => () => { void client.cancelQueries(); client.clear() }, [client])
  return <main className="instance-administration"><div className="instance-administration__shell"><QueryClientProvider client={client}><Administration /></QueryClientProvider></div></main>
}
